import { ICategory, ILayers } from 'contexts/@types/Layers';

export const SEARCH_RESULTS_CATEGORY_ID = 1000;
export const SEARCH_RESULTS_THEME_ID = 1000;

export type LayerPreference = {
  id: number;
  layer_filter_id?: number | null;
  layername: string;
};

export type OpenLayerPreferences = {
  categories: number[];
  themes: number[];
  subthemes: number[];
};

export type SearchResultLayerPreference = {
  original: LayerPreference;
  filter: string;
  layer_filter_id: number;
  layertitle: string;
};

export function parsePreference<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export function serializeLayer(layer: ILayers): LayerPreference {
  return {
    id: layer.id,
    layer_filter_id: layer.layer_filter_id ?? null,
    layername: layer.layername,
  };
}

export function sameLayerPreference(layer: ILayers, pref: LayerPreference) {
  return (
    layer.id === pref.id &&
    layer.layername === pref.layername &&
    (layer.layer_filter_id ?? null) === (pref.layer_filter_id ?? null)
  );
}

export function serializeActiveLayers(layers: ILayers[]): LayerPreference[] {
  return layers.map(serializeLayer);
}

export function serializeOpenLayers(categories: ICategory[]): OpenLayerPreferences {
  return {
    categories: categories.filter(category => category.opened).map(category => category.id_categoria),
    themes: categories.flatMap(category =>
      category.temas.filter(theme => theme.opened).map(theme => theme.id_tema)
    ),
    subthemes: categories.flatMap(category =>
      category.temas.flatMap(theme =>
        (theme.subtema ?? []).filter(subtheme => subtheme.opened).map(subtheme => subtheme.id_subtema)
      )
    ),
  };
}

export function applyOpenLayerPreferences(categories: ICategory[], preferences: OpenLayerPreferences): ICategory[] {
  return categories.map(category => ({
    ...category,
    opened: preferences.categories?.includes(category.id_categoria) ?? false,
    temas: category.temas.map(theme => ({
      ...theme,
      opened: preferences.themes?.includes(theme.id_tema) ?? false,
      subtema: theme.subtema?.map(subtheme => ({
        ...subtheme,
        opened: preferences.subthemes?.includes(subtheme.id_subtema) ?? false,
      })),
    })),
  }));
}

export function getAllCategoryLayers(categories: ICategory[]): ILayers[] {
  return categories.flatMap(category =>
    category.temas.flatMap(theme => [
      ...(theme.layers ?? []),
      ...(theme.subtema?.flatMap(subtheme => subtheme.layers ?? []) ?? []),
    ])
  );
}

export function buildQueryLabel(query: string): string {
  if (!query) return '';

  return query
    .replace(/\s+/g, '_')
    .replace(/['"%()]/g, '')
    .replace(/AND|OR/gi, '')
    .replace(/=/g, '_')
    .replace(/<>/g, 'diferente_de')
    .replace(/>=/g, 'maior_igual_a')
    .replace(/<=/g, 'menor_igual_a')
    .replace(/>/g, 'maior_que')
    .replace(/</g, 'menor_que')
    .replace(/LIKE/gi, 'like')
    .replace(/IS_NULL/gi, 'null');
}

export function createSearchResultLayer(original: ILayers, filter: string, preference?: Partial<SearchResultLayerPreference>): ILayers {
  const baseLayerTitle = original.layer_alias || original.layertitle;
  const layertitle = preference?.layertitle || `${baseLayerTitle}_resultado_pesquisa ${buildQueryLabel(filter)}`;
  const layerFilterId = preference?.layer_filter_id ?? Math.floor(Math.random() * (10000 - 1000 + 1)) + 1000;

  return {
    ...JSON.parse(JSON.stringify(original)),
    layertitle,
    layer_alias: layertitle,
    original_query_filter: original.layer_query_filter,
    layer_query_filter: original.layer_query_filter ? `[${original.layer_query_filter}] AND [${filter}]` : filter,
    nova_camada: false,
    layer_filter_id: layerFilterId,
    visible: true,
  };
}

export function createSearchResultPreference(original: ILayers, filter: string, result: ILayers): SearchResultLayerPreference {
  return {
    original: serializeLayer(original),
    filter,
    layer_filter_id: result.layer_filter_id,
    layertitle: result.layertitle,
  };
}

export function buildSearchResultCategories(preferences: SearchResultLayerPreference[], baseLayers: ILayers[]): ICategory[] {
  const layers = preferences
    .map(preference => {
      const original = baseLayers.find(layer => sameLayerPreference(layer, preference.original));
      if (!original || !preference.filter) return null;
      return createSearchResultLayer(original, preference.filter, preference);
    })
    .filter((layer): layer is ILayers => !!layer);

  if (!layers.length) return [];

  const camadasPesquisadas = Array.from(new Set(layers.map(layer => layer.layer_alias?.split('_resultado_pesquisa')[0] || layer.layertitle)));

  return [{
    id_categoria: SEARCH_RESULTS_CATEGORY_ID,
    camadas_pesquisadas: camadasPesquisadas,
    nm_categoria: 'Resultados de pesquisas',
    ord_categoria: SEARCH_RESULTS_CATEGORY_ID,
    temas: [{
      id_tema: SEARCH_RESULTS_THEME_ID,
      nm_tema: 'Pesquisas Recentes',
      ord_tema: SEARCH_RESULTS_THEME_ID,
      visivel: true,
      categoria_id: SEARCH_RESULTS_CATEGORY_ID,
      subtema: [],
      layers,
    }],
  }];
}

